import * as React from "react";
import { motion } from "framer-motion";
import { Check, Loader2 } from "lucide-react";
import type { QaProgressMetrics, QaPhase } from "./useQaProgress";

interface TaskDef {
  id: QaPhase;
  label: string;
  activeLabel?: (metrics: QaProgressMetrics) => string;
  completedLabel?: (metrics: QaProgressMetrics) => string;
  modes: ("PROSE" | "LIST")[];
  /** Hide the step unless the run actually entered it (e.g. only large collections batch). */
  visibleWhen?: (metrics: QaProgressMetrics) => boolean;
}

const TASKS: TaskDef[] = [
  {
    id: "routing",
    label: "Analyzing Query",
    modes: ["PROSE", "LIST"],
  },
  {
    id: "rewriting",
    label: "Rewriting Query",
    modes: ["PROSE", "LIST"],
  },
  {
    id: "detecting_fields",
    label: "Detecting Relevant Fields",
    completedLabel: m => {
      const count = m.detectedFields.length;
      if (count === 0) return "All columns selected";
      return `${count} relevant column${count !== 1 ? "s" : ""}`;
    },
    modes: ["PROSE", "LIST"],
  },
  {
    id: "scoping",
    label: "Deciding how much to read",
    completedLabel: m => SCOPE_LABELS[m.scopeStrategy ?? "EXHAUSTIVE"] + (m.scopeHyde ? " (semantic)" : ""),
    modes: ["PROSE"],
  },
  {
    id: "retrieving",
    label: "Retrieving Documents",
    completedLabel: m => `${m.documentCount} document${m.documentCount !== 1 ? "s" : ""} found`,
    modes: ["LIST"],
  },
  {
    id: "retrieving",
    label: "Selecting documents",
    completedLabel: m => `${m.selectedDocuments} document${m.selectedDocuments !== 1 ? "s" : ""} selected`,
    modes: ["PROSE"],
    visibleWhen: m => m.scopeStrategy !== null && m.scopeStrategy !== "EXHAUSTIVE",
  },
  {
    id: "analyzing",
    label: "Analyzing Results",
    modes: ["LIST"],
  },
  {
    id: "reading_batches",
    label: "Reading documents in batches",
    activeLabel: m =>
      m.batchTotal > 0 ? `Reading batch ${m.batchDone + 1} of ${m.batchTotal}` : "Reading documents in batches",
    completedLabel: m => `${m.batchTotal} batch${m.batchTotal !== 1 ? "es" : ""} read`,
    modes: ["PROSE"],
    visibleWhen: m => m.mapReduce,
  },
  {
    id: "generating",
    label: "Generating Response",
    modes: ["PROSE"],
  },
];

const SCOPE_LABELS: Record<string, string> = {
  EXHAUSTIVE: "Reading the whole collection",
  CONCEPT: "Searching for matching content",
  LOOKUP: "Reading the named document",
  STRUCTURED: "Computing from the grid",
};

const PHASE_ORDER: QaPhase[] = [
  "routing",
  "rewriting",
  "detecting_fields",
  "scoping",
  "retrieving",
  "analyzing",
  "reading_batches",
  "generating",
  "complete",
];

type TaskStatus = "pending" | "active" | "completed";

const getStatus = (taskPhase: QaPhase, currentPhase: QaPhase): TaskStatus => {
  const taskIdx = PHASE_ORDER.indexOf(taskPhase);
  const currentIdx = PHASE_ORDER.indexOf(currentPhase);
  if (currentIdx > taskIdx) return "completed";
  if (currentIdx === taskIdx) return "active";
  return "pending";
};

const formatTime = (ms: number): string => `${(ms / 1000).toFixed(1)}s`;

const useElapsedTime = (phase: QaPhase): number => {
  const [elapsed, setElapsed] = React.useState(0);
  const startRef = React.useRef(Date.now());

  React.useEffect(() => {
    startRef.current = Date.now();
    setElapsed(0);
    if (phase === "idle" || phase === "complete") return;
    const timer = setInterval(() => setElapsed(Date.now() - startRef.current), 100);
    return () => clearInterval(timer);
  }, [phase]);

  return elapsed;
};

interface Props {
  metrics: QaProgressMetrics;
}

export const SnapshotQaProgress: React.FC<Props> = React.memo(({ metrics }) => {
  const visibleTasks = TASKS.filter(task => {
    if (!metrics.routingMode) return task.id === "routing" || task.id === "rewriting";
    if (task.visibleWhen && !task.visibleWhen(metrics)) return false;
    return task.modes.includes(metrics.routingMode);
  });
  const elapsed = useElapsedTime(metrics.phase);

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className="mb-3 flex flex-col gap-2 rounded-lg border bg-muted/30 px-4 py-3"
    >
      {visibleTasks.map(task => {
        const status = getStatus(task.id, metrics.phase);
        const label =
          status === "completed" && task.completedLabel
            ? task.completedLabel(metrics)
            : status === "active" && task.activeLabel
              ? task.activeLabel(metrics)
              : task.label;

        const showFields =
          task.id === "detecting_fields" &&
          status === "completed" &&
          metrics.detectedFields.length > 0 &&
          metrics.detectedFields.length < 20;

        return (
          <div key={`${task.id}-${task.label}`} className="flex flex-col gap-1">
            <div className="flex items-center justify-between gap-2 text-xs">
              <div className="flex items-center gap-2">
                {status === "completed" && <Check className="h-3.5 w-3.5 text-green-600" />}
                {status === "active" && <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />}
                {status === "pending" && <div className="h-3.5 w-3.5 rounded-full border border-muted-foreground/30" />}
                <span
                  className={
                    status === "pending"
                      ? "text-muted-foreground/50"
                      : status === "active"
                        ? "font-medium text-foreground"
                        : "text-muted-foreground"
                  }
                >
                  {label}
                </span>
              </div>
              {status === "active" && (
                <span className="shrink-0 font-mono text-[10px] text-muted-foreground">{formatTime(elapsed)}</span>
              )}
              {status === "completed" && metrics.phaseTimings[task.id] != null && (
                <span className="shrink-0 font-mono text-[10px] text-muted-foreground">
                  {formatTime(metrics.phaseTimings[task.id])}
                </span>
              )}
            </div>
            {showFields && (
              <div className="ml-5.5 flex flex-wrap gap-1 pl-0.5">
                {metrics.detectedFields.map(field => (
                  <span key={field} className="rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
                    {field}
                  </span>
                ))}
              </div>
            )}
            {task.id === "analyzing" && status === "active" && metrics.documentCount > 0 && (
              <div className="ml-5.5 flex flex-col gap-1.5 pl-0.5">
                <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                  <motion.div
                    className="h-full rounded-full bg-primary"
                    initial={{ width: 0 }}
                    animate={{ width: `${Math.min((metrics.analyzedCount / metrics.documentCount) * 100, 100)}%` }}
                    transition={{ duration: 0.3, ease: "easeOut" }}
                  />
                </div>
                <span className="text-[10px] text-muted-foreground">
                  Analyzing: <span className="font-semibold text-foreground">{metrics.analyzedCount}</span> of{" "}
                  <span className="font-semibold text-foreground">{metrics.documentCount}</span>
                </span>
              </div>
            )}
          </div>
        );
      })}
    </motion.div>
  );
});

SnapshotQaProgress.displayName = "SnapshotQaProgress";
